/**
 * Converts a Unix timestamp (seconds since epoch) to a Date object.
 */
export function fromUnixTimestamp(seconds: number): Date {
   return new Date(seconds * 1000);
}

/**
 * Formats a date relative to now using natural language:
 * "yesterday", "2 days ago", "3 weeks ago", "2 months ago", etc.
 */
export function formatRelativeDate(date: Date, locale: string = 'en'): string {
   const { value, unit } = findTimeDistance(date);
   return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(value, unit);
}

/**
 * Formats the distance between a date and now as a duration:
 * "today", "3 days", "2 weeks", "8 months", "3 years", etc.
 */
export function formatDuration(date: Date, locale: string = 'en'): string {
   const { value, unit } = findTimeDistance(date);
   const absValue = Math.abs(value);
   if (absValue === 0) {
      return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(0, unit);
   }
   const singularUnit = unit.replace(/s$/, '');
   return new Intl.NumberFormat(locale, {
      style: 'unit',
      unit: singularUnit,
      unitDisplay: 'long',
   }).format(absValue);
}

interface HumanizeDateOptions {
   short?: boolean;
   locale?: string;
}

/**
 * Formats a date for display: "yesterday", "today", "tomorrow" for nearby
 * dates, and "Apr 19" or "April 19" for others. Includes the year only
 * when the date is not in the current year.
 */
export function humanizeDate(date: Date, options?: HumanizeDateOptions): string {
   const days = daysFromNow(date);
   if (days >= -1 && days <= 1) {
      return new Intl.RelativeTimeFormat(options?.locale, { numeric: 'auto' }).format(days, 'day');
   }
   const sameYear = date.getFullYear() === new Date().getFullYear();
   return date.toLocaleDateString(options?.locale, {
      month: options?.short ? 'short' : 'long',
      day: 'numeric',
      ...(sameYear ? {} : { year: 'numeric' }),
   });
}

/**
 * Returns the signed number of days between a date and today.
 * Positive means future, negative means past.
 */
export function daysFromNow(date: Date): number {
   const normalizedDate = new Date(date);
   normalizedDate.setHours(0, 0, 0, 0);
   const today = new Date();
   today.setHours(0, 0, 0, 0);
   return Math.round((normalizedDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

const DIVISIONS: Array<{ amount: number; name: Intl.RelativeTimeFormatUnit }> = [
   { amount: 7, name: 'days' },
   { amount: 4.345_24, name: 'weeks' },
   { amount: 12, name: 'months' },
   { amount: Number.POSITIVE_INFINITY, name: 'years' },
];

function findTimeDistance(date: Date): { value: number; unit: Intl.RelativeTimeFormatUnit } {
   const normalizedDate = new Date(date);
   normalizedDate.setHours(0, 0, 0, 0);
   const today = new Date();
   today.setHours(0, 0, 0, 0);
   let duration = (normalizedDate.getTime() - today.getTime()) / 1000 / 60 / 60 / 24;

   for (const division of DIVISIONS) {
      if (Math.abs(duration) < division.amount) {
         return { value: Math.round(duration), unit: division.name };
      }
      duration /= division.amount;
   }
   return { value: Math.round(duration), unit: 'years' };
}
